import React, { useState } from 'react';
import { Trans, useTranslation } from '../../i18n';
import { CultureFitCategory } from '../../typings';
import summaryStyles from '../Summary/CultureFitSummary/SummaryWorkPreferences/styles.module.css';
import styles from './styles.module.css';
import { getWorkPreferencesFormattedData, getHighestCat } from './utils';
import WorkPreferenceBarChart from '../../components/WorkPreferenceBarChart';
import SectionWithHeader from '../../components/SectionWithHeader';
import globalStyles from '../../styles/global.module.css';
import QuestionsToAskModal from '../../components/QuestionsToAskModal';
import { useReportData } from '../../GlobalStateContext';

interface CultureFitWorkPreferencesProps {
    companyName: string;
    cultureFitCategories: CultureFitCategory[];
    hasBaseline: boolean;
    isWeb: boolean;
}

const CultureFitWorkPreferences = (props: CultureFitWorkPreferencesProps) => {
    const {
        companyName,
        cultureFitCategories,
        hasBaseline,
        isWeb,
    } = props;
    const { t } = useTranslation();
    const [showInfoModal, setShowInfoModal] = useState<boolean>(false);
    const { state } = useReportData();
    const { baselineRange } = state.reportData;

    const data = getWorkPreferencesFormattedData(
        companyName,
        cultureFitCategories,
        t,
        hasBaseline,
    );

    return (
        <>
            {isWeb && !data[1] && (
                <SectionWithHeader
                    subTitle={t('CULTURAL_FIT.WORK_PREFERENCES.TITLE')}
                    hasHeader={!isWeb}
                    handleInfoDetails={() => setShowInfoModal(true)}
                    isWeb={isWeb}>
                    <></>
                </SectionWithHeader>
            )}
            {!isWeb && (
                <p className={`${globalStyles.text}`}>
                    <Trans
                        i18nKey="CULTURAL_FIT.WORK_PREFERENCES.EXPLANATION_WHEN_NO_BASELINE"
                        components={{ bold: <strong /> }}
                    />
                </p>
            )}
            <SectionWithHeader
                chartTitle={
                    data[1]
                        ? t('CULTURAL_FIT.WORK_PREFERENCES.COMPARISON_TITLE')
                        : t('CULTURAL_FIT.WORK_PREFERENCES.YOUR_SCORE_TITLE')
                }
                hasHeader={!isWeb}
                scoreChart={
                    <div
                        className={
                            summaryStyles.workPreferencesPreferredEnvDetailedViewWrap
                        }>
                        <div
                            className={`${summaryStyles.workPreferencesPreferredEnv} ${summaryStyles.workPreferencesPreferredEnvDetailedView}`}>
                            <span
                                className={
                                    summaryStyles.workPreferencesPreferredEnvText
                                }>
                                {t(
                                    'CULTURAL_FIT.WORK_PREFERENCES.YOUR_ENVIRONMENT_DESC',
                                )}
                            </span>
                            <span
                                className={
                                    summaryStyles.workPreferencesPreferredEnvLabel
                                }>
                                {t(
                                    `CULTURAL_FIT.WORK_PREFERENCES.CATEGORIES.${getHighestCat(
                                        data[0],
                                        baselineRange,
                                    ).toUpperCase()}`,
                                )}
                            </span>
                            <span
                                className={
                                    summaryStyles.workPreferencesPreferredEnvTitle
                                }>
                                {t(
                                    `CULTURAL_FIT.WORK_PREFERENCES.YOUR_ENVIRONMENT_DESC_1`,
                                )}
                            </span>
                            <ul
                                className={
                                    summaryStyles.workPreferencesPreferredEnvTitle
                                }
                                style={{
                                    paddingLeft: '24px',
                                    listStyle: 'disc',
                                }}>
                                <li>
                                    {t(
                                        `CULTURAL_FIT.WORK_PREFERENCES.CATEGORIES.${getHighestCat(
                                            data[0],
                                            baselineRange,
                                        ).toUpperCase()}_YOUR_DESC_1`,
                                    )}
                                </li>
                                <li>
                                    {t(
                                        `CULTURAL_FIT.WORK_PREFERENCES.CATEGORIES.${getHighestCat(
                                            data[0],
                                            baselineRange,
                                        ).toUpperCase()}_YOUR_DESC_2`,
                                    )}
                                </li>
                                <li>
                                    {t(
                                        `CULTURAL_FIT.WORK_PREFERENCES.CATEGORIES.${getHighestCat(
                                            data[0],
                                            baselineRange,
                                        ).toUpperCase()}_YOUR_DESC_3`,
                                    )}
                                </li>
                                <li>
                                    {t(
                                        `CULTURAL_FIT.WORK_PREFERENCES.CATEGORIES.${getHighestCat(
                                            data[0],
                                            baselineRange,
                                        ).toUpperCase()}_YOUR_DESC_4`,
                                    )}
                                </li>
                            </ul>
                            <span
                                className={
                                    summaryStyles.workPreferencesPreferredEnvTitle
                                }>
                                {t(
                                    `CULTURAL_FIT.WORK_PREFERENCES.CATEGORIES.${getHighestCat(
                                        data[0],
                                        baselineRange,
                                    ).toUpperCase()}_YOUR_DESC_5`,
                                )}
                            </span>
                        </div>
                        {data[1] && (
                            <div
                                className={`${summaryStyles.workPreferencesPreferredEnv} ${summaryStyles.workPreferencesPreferredEnvDetailedView}`}>
                                <span
                                    className={
                                        summaryStyles.workPreferencesPreferredEnvText
                                    }>
                                    {t(
                                        'CULTURAL_FIT.WORK_PREFERENCES.COMPANY_ENVIRONMENT_DESC',
                                        { company_name: companyName },
                                    )}
                                </span>
                                <span
                                    className={
                                        summaryStyles.workPreferencesPreferredEnvLabel
                                    }>
                                    {t(
                                        `CULTURAL_FIT.WORK_PREFERENCES.CATEGORIES.${getHighestCat(
                                            data[1],
                                            baselineRange,
                                        ).toUpperCase()}`,
                                    )}
                                </span>
                                <span
                                    className={
                                        summaryStyles.workPreferencesPreferredEnvTitle
                                    }>
                                    {t(
                                        `CULTURAL_FIT.WORK_PREFERENCES.COMPANY_ENVIRONMENT_DESC_1`,
                                    )}
                                </span>
                                <ul
                                    className={
                                        summaryStyles.workPreferencesPreferredEnvTitle
                                    }
                                    style={{
                                        paddingLeft: '24px',
                                        listStyle: 'disc',
                                    }}>
                                    <li>
                                        {t(
                                            `CULTURAL_FIT.WORK_PREFERENCES.CATEGORIES.${getHighestCat(
                                                data[1],
                                                baselineRange,
                                            ).toUpperCase()}_COMPANY_DESC_1`,
                                        )}
                                    </li>
                                    <li>
                                        {t(
                                            `CULTURAL_FIT.WORK_PREFERENCES.CATEGORIES.${getHighestCat(
                                                data[1],
                                                baselineRange,
                                            ).toUpperCase()}_COMPANY_DESC_2`,
                                        )}
                                    </li>
                                    <li>
                                        {t(
                                            `CULTURAL_FIT.WORK_PREFERENCES.CATEGORIES.${getHighestCat(
                                                data[1],
                                                baselineRange,
                                            ).toUpperCase()}_COMPANY_DESC_3`,
                                        )}
                                    </li>
                                    <li>
                                        {t(
                                            `CULTURAL_FIT.WORK_PREFERENCES.CATEGORIES.${getHighestCat(
                                                data[1],
                                                baselineRange,
                                            ).toUpperCase()}_COMPANY_DESC_4`,
                                        )}
                                    </li>
                                </ul>
                                <span
                                    className={
                                        summaryStyles.workPreferencesPreferredEnvTitle
                                    }>
                                    {t(
                                        `CULTURAL_FIT.WORK_PREFERENCES.CATEGORIES.${getHighestCat(
                                            data[1],
                                            baselineRange,
                                        ).toUpperCase()}_COMPANY_DESC_5`,
                                    )}
                                </span>
                            </div>
                        )}
                    </div>
                }>
                <div className={styles.workPreferencesChartWrap}>
                    <WorkPreferenceBarChart data={data} />
                </div>
            </SectionWithHeader>
            {showInfoModal && isWeb && (
                <QuestionsToAskModal
                    title={t('GENERAL_SCORE_MEANING')}
                    closeScanDetails={() => setShowInfoModal(false)}>
                    <p className={`${globalStyles.text}`}>
                        <Trans
                            i18nKey="CULTURAL_FIT.WORK_PREFERENCES.EXPLANATION_WHEN_NO_BASELINE"
                            components={{ bold: <strong /> }}
                        />
                    </p>
                </QuestionsToAskModal>
            )}
        </>
    );
};

export default CultureFitWorkPreferences;
